import React from "react";
import { Building2, FileText, Settings, LogOut, User as UserIcon, BarChart3 } from "lucide-react";
import { Button } from "../ui/button";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from "../ui/dropdown-menu";
import { cn } from "../ui/utils"; // if you have a cn helper; otherwise remove and inline classes

type Section = "overview" | "details" | "monitora";

interface PortalHeaderProps {
  activeSection: Section;
  setActiveSection: (section: Section) => void;

  onGoHome: () => void;
  onLogout?: () => void;
  onPortalSettings?: () => void;
  onSwitchToStructure?: () => void;

  portalName?: string;          // e.g. "Visit Bergamo"
  derivedRoleLabel?: string;    // e.g. "Portal Manager"
  user?: { email?: string | null } | null;

  className?: string;
  branding?: "visit-bergamo" | "default";
}

export default function PortalHeader({
  activeSection,
  setActiveSection,
  onGoHome,
  onLogout,
  onPortalSettings,
  onSwitchToStructure,
  portalName = "Visit Bergamo",
  derivedRoleLabel = "Gestore Portale",
  user,
  className,
  branding = "default",
}: PortalHeaderProps) {
  const isVisitBergamo = branding === "visit-bergamo" || portalName.toLowerCase().includes("bergamo");
  const visitPalette = {
    base: "#0f0f0f",
    border: "#1c1c1c",
    accent: "#ec2b3c",
    text: "#ffffff",
    muted: "#d9d9d9",
  };

  const tabClasses = (section: Section) => {
    if (!isVisitBergamo) {
      return (
        "px-4 py-2 " +
        (activeSection === section
          ? "bg-white text-[#f65009] border-white hover:bg-white/95 hover:text-[#f65009] focus:text-[#f65009] shadow-sm font-medium"
          : "bg-white/10 backdrop-blur-sm border-white text-white hover:text-white hover:bg-white/20 hover:border-white hover:shadow-lg hover:shadow-orange-500/20 font-medium")
      );
    }
    const isActive = activeSection === section;
    return [
      "px-4 py-2 rounded-md font-medium transition-all",
      isActive
        ? "text-white shadow-sm"
        : "text-white border border-white/12 hover:text-white hover:border-white/30",
    ].join(" ");
  };

  const tabStyle = (section: Section) =>
    isVisitBergamo && activeSection === section
      ? { backgroundColor: visitPalette.accent, borderColor: visitPalette.accent }
      : isVisitBergamo
        ? { backgroundColor: "transparent" }
        : undefined;

  return (
    <header
      className={cn(
        isVisitBergamo ? "p-4 shadow-lg border-b" : "bg-gradient-travelin border-b border-blue-200/30 p-4 shadow-lg",
        className
      )}
      style={isVisitBergamo ? { backgroundColor: visitPalette.base, borderColor: visitPalette.border } : undefined}
    >
      <div className="max-w-7xl mx-auto space-y-3">
        <div className="flex justify-center">
          <img
            src={isVisitBergamo ? "/visit-bergamo-logo.svg" : "/travelin-logo.svg"}
            alt={isVisitBergamo ? "Visit Bergamo" : "Travelin"}
            className="h-10 w-auto object-contain"
          />
        </div>
        <div className="grid grid-cols-3 items-center">
          {/* Brand + back to Home */}
          <button
            type="button"
            onClick={onGoHome}
            title="Torna alla home"
            className="flex items-center gap-3 group select-none focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-400 rounded-md cursor-pointer"
            style={{ background: "transparent" }}
          >
            <div className="text-white text-left">
              <h1 className="text-xl font-semibold leading-tight group-hover:underline">
                {isVisitBergamo ? "Visit Bergamo" : "Dashboard gestione portale"}
              </h1>
              <p className="text-ss text-white">
                Area Operatori - Gestore Portale
              </p>
              <div className="mt-1 flex items-center gap-2 text-ss text-white/80">
                <span>Offerto da</span>
                <img
                  src="/travelin-logo.svg"
                  alt="Travelin"
                  className="h-4 w-auto rounded-sm bg-white px-1 py-0.5"
                />
              </div>
            </div>
          </button>

          {/* Section Switcher */}
          <div className="flex items-center justify-center gap-2">
            <Button
              variant={activeSection === "overview" ? "secondary" : "outline"}
              onClick={() => setActiveSection("overview")}
              className={tabClasses("overview")}
              style={tabStyle("overview")}
            >
              <Building2 className="w-4 h-4 mr-2" />
              Panoramica
            </Button>
            <Button
              variant={activeSection === "details" ? "secondary" : "outline"}
              onClick={() => setActiveSection("details")}
              className={tabClasses("details")}
              style={tabStyle("details")}
            >
              <FileText className="w-4 h-4 mr-2" />
              Dettagli strutture
            </Button>
            <Button
              variant={activeSection === "monitora" ? "secondary" : "outline"}
              onClick={() => setActiveSection("monitora")}
              className={tabClasses("monitora")}
              style={tabStyle("monitora")}
            >
              <BarChart3 className="w-4 h-4 mr-2" />
              Monitora
            </Button>
          </div>

          {/* Right Side: portal/role + user menu */}
          <div className="flex items-center justify-end gap-3">
            <div className="text-right">
              <p className="text-s font-medium text-white">{portalName}</p>
              <p className={`text-ss ${isVisitBergamo ? "text-white" : "text-blue-100"}`}>
                {derivedRoleLabel}
              </p>
            </div>

            {user && (
              <div className="text-right mr-4 hidden sm:block">
                <p className="text-s font-medium text-white">
                  {user.email || "Accesso effettuato"}
                </p>
                <p className={`text-ss ${isVisitBergamo ? "text-white" : "text-blue-100"}`}>
                  Accesso effettuato
                </p>
              </div>
            )}

            <DropdownMenu>
              <DropdownMenuTrigger className="h-10 w-10 rounded-full p-0 bg-transparent border-0 cursor-pointer hover:bg-white/10 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500">
                <div className={`w-8 h-8 rounded-full ${isVisitBergamo ? "bg-white/10 border border-white/15 text-white" : "bg-white text-travelin-blue"} flex items-center justify-center`}>
                  <UserIcon className="w-4 h-4" />
                </div>
              </DropdownMenuTrigger>
              <DropdownMenuContent
                align="end"
                className={isVisitBergamo ? "w-56 bg-[#0b0b0b] text-white border border-[#1c1c1c]" : "w-56"}
                style={isVisitBergamo ? { backgroundColor: "#0b0b0b", borderColor: "#1c1c1c" } : undefined}
              >
                {/* <DropdownMenuLabel className="pb-2">
                  <div className="flex flex-col space-y-1">
                    <p className="text-s font-medium">{portalName}</p>
                    <p className={`text-ss ${isVisitBergamo ? "text-white/70" : "text-muted-foreground"}`}>
                      {derivedRoleLabel}
                    </p>
                  </div>
                </DropdownMenuLabel> */}
                <DropdownMenuSeparator />

                {onSwitchToStructure && (
                  <DropdownMenuItem
                    onClick={onSwitchToStructure}
                    className="cursor-pointer"
                  >
                    <Settings className="w-4 h-4 mr-3" />
                    <span>Passa alla dashboard struttura</span>
                  </DropdownMenuItem>
                )}

                {onLogout && (
                  <DropdownMenuItem
                    onClick={onLogout}
                    className="cursor-pointer text-red-600"
                  >
                    <LogOut className="w-4 h-4 mr-3" />
                    <span>Esci</span>
                  </DropdownMenuItem>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </div>
    </header>
  );
}
